import { useState, useEffect, useCallback, useRef } from 'react';
import QRCode from 'qrcode';
import axios from 'axios';
import '../admin.css';

const api = axios.create({
  baseURL: process.env.NODE_ENV === 'production' ? 'https://muki684.com' : 'https://muki684.com',
});
const authApi = token => ({ headers: { Authorization: `Bearer ${token}` } });

/* ---- Helpers ---- */
const MENU_CATS = [
  'breakfast','skewers','soup','healthy','sandwiches','lunch','sides',
  'dessert','hot_drinks','cold_drinks','mocktails','smoothies','juices',
  'tea','milkshakes','lemonades','ice_cream',
];
function catLabel(c) {
  return c.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/* ---- Toast hook ---- */
function useToast() {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((msg, type = 'info') => {
    const id = Date.now();
    setToasts(p => [...p, { id, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3000);
  }, []);
  return [toasts, add];
}
function Toasts({ list }) {
  return (
    <div id="admin-toasts">
      {list.map(t => <div key={t.id} className={`admin-toast ${t.type}`}>{t.msg}</div>)}
    </div>
  );
}

/* ---- Table utilities ---- */
const PAGE_SIZE = 8;

function usePaginatedSearch(list, searchFn) {
  const [search, setSearch] = useState('');
  const [page, setPage]     = useState(1);

  const filtered   = search.trim()
    ? list.filter(item => searchFn(item, search.toLowerCase()))
    : list;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paged      = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  function onSearch(v) { setSearch(v); setPage(1); }

  return { search, onSearch, page: safePage, setPage, paged, filtered, totalPages };
}

function TableSearch({ value, onChange, placeholder = 'Search…' }) {
  return (
    <div className="table-search">
      <i className="fa-solid fa-magnifying-glass" />
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
      {value && (
        <button className="table-search-clear" onClick={() => onChange('')} aria-label="Clear">
          <i className="fa-solid fa-xmark" />
        </button>
      )}
    </div>
  );
}

function Pagination({ page, totalPages, setPage, total, filtered }) {
  if (totalPages <= 1 && filtered === total) return null;
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to   = Math.min(page * PAGE_SIZE, filtered);
  return (
    <div className="table-pagination">
      <span className="pagination-info">
        {filtered === 0 ? 'No results' : `${from}–${to} of ${filtered}`}
        {filtered !== total ? ` (filtered from ${total})` : ''}
      </span>
      <div className="pagination-btns">
        <button disabled={page === 1} onClick={() => setPage(p => p - 1)} aria-label="Previous">
          <i className="fa-solid fa-chevron-left" />
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
          .reduce((acc, n, i, arr) => {
            if (i > 0 && arr[i - 1] !== n - 1) acc.push('…');
            acc.push(n);
            return acc;
          }, [])
          .map((n, i) => n === '…'
            ? <span key={`ellipsis-${i}`} className="pagination-ellipsis">…</span>
            : <button key={n} className={page === n ? 'active' : ''} onClick={() => setPage(n)}>{n}</button>
          )
        }
        <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} aria-label="Next">
          <i className="fa-solid fa-chevron-right" />
        </button>
      </div>
    </div>
  );
}

/* ================================================================
   LOGIN / REGISTER
================================================================ */
function LoginScreen({ onLogin }) {
  const [err, setErr]         = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault(); setLoading(true); setErr('');
    const { username, password } = Object.fromEntries(new FormData(e.target));
    try {
      const { data } = await api.post('/api/users/login', { username, password });
      if (data.token) onLogin(data.token);
    } catch (err) {
      setErr(err.response?.data?.error || 'Invalid credentials.');
    }
    setLoading(false);
  }

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-logo">Muki 684 <span>Admin</span></div>

        {err && <div className="login-error">{err}</div>}

        <form onSubmit={handleSubmit}>
          <div className="admin-form-group">
            <label>Username</label>
            <input name="username" required autoFocus autoComplete="username" />
          </div>
          <div className="admin-form-group">
            <label>Password</label>
            <input name="password" type="password" required autoComplete="current-password" />
          </div>
          <button type="submit" className="admin-btn admin-btn-primary"
            style={{ width:'100%', justifyContent:'center', marginTop:'8px' }}
            disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <a href="/" className="login-back">← Back to website</a>
      </div>
    </div>
  );
}

/* ================================================================
   SIDEBAR
================================================================ */
function Sidebar({ page, setPage, onLogout, open, setOpen }) {
  const links = [
    { id:'overview',     icon:'fa-chart-line',    label:'Overview' },
    { id:'orders',       icon:'fa-bag-shopping',  label:'Orders' },
    { id:'gallery',      icon:'fa-images',        label:'Gallery' },
    { id:'reviews',      icon:'fa-star',          label:'Reviews' },
    { id:'menu',         icon:'fa-utensils',      label:'Menu' },
    { id:'reservations', icon:'fa-calendar',      label:'Reservations' },
    { id:'contacts',     icon:'fa-envelope',      label:'Messages' },
    { id:'menu-pdf',     icon:'fa-file-pdf',      label:'Menu PDF' },
    { id:'qr-codes',     icon:'fa-qrcode',        label:'QR Codes' },
  ];
  return (
    <aside className={`admin-sidebar${open ? ' open' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-logo">Muki 684 <span>Admin</span></div>
        <button className="sidebar-close" onClick={() => setOpen(false)}>×</button>
      </div>
      <nav className="sidebar-nav">
        {links.map(l => (
          <button key={l.id} className={`sidebar-link${page===l.id?' active':''}`}
            onClick={() => { setPage(l.id); setOpen(false); }}>
            <i className={`fa-solid ${l.icon}`} />{l.label}
          </button>
        ))}
      </nav>
      <div className="sidebar-footer">
        <a href="/" className="sidebar-link"><i className="fa-solid fa-house" />View Site</a>
        <button className="sidebar-link logout" onClick={onLogout}>
          <i className="fa-solid fa-right-from-bracket" />Logout
        </button>
      </div>
    </aside>
  );
}

/* ================================================================
   OVERVIEW
================================================================ */
function Overview({ token }) {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const cfg = authApi(token);

  useEffect(() => {
    Promise.all([
      api.get('/api/stats', cfg),
      api.get('/api/reservations', cfg),
    ]).then(([s, r]) => {
      setStats(s.data);
      setRecent(Array.isArray(r.data) ? r.data.slice(0,6) : []);
    }).catch(console.error);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!stats) return <div className="admin-loading"><i className="fa-solid fa-circle-notch fa-spin" /></div>;

  return (
    <>
      <div className="stats-grid">
        {[
          { cls:'stat-gold',   icon:'fa-utensils',       label:'Menu Items',           val: stats.menuItems },
          { cls:'stat-orange', icon:'fa-bag-shopping',   label:'Pending Orders',       val: stats.pendingOrders },
          { cls:'stat-purple', icon:'fa-star',           label:'Pending Reviews',      val: stats.pendingReviews },
          { cls:'stat-blue',   icon:'fa-calendar',       label:'Pending Reservations', val: stats.pendingReservations },
          { cls:'stat-green',  icon:'fa-envelope',       label:'Messages',             val: stats.newMessages },
        ].map(s => (
          <div key={s.label} className={`stat-card ${s.cls}`}>
            <div className="stat-card-icon"><i className={`fa-solid ${s.icon}`} /></div>
            <div>
              <div className="stat-card-value">{s.val}</div>
              <div className="stat-card-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="admin-card">
        <div className="admin-card-header"><h3>Recent Reservations</h3></div>
        <table className="admin-table">
          <thead><tr><th>Guest</th><th>Date & Time</th><th>Guests</th><th>Status</th></tr></thead>
          <tbody>
            {recent.map(r => (
              <tr key={r.id}>
                <td><strong>{r.name}</strong></td>
                <td>{fmtDate(r.date)} {r.time?.slice(0,5)}</td>
                <td>{r.guests}</td>
                <td><span className={`badge badge-${r.status}`}>{r.status}</span></td>
              </tr>
            ))}
            {!recent.length && (
              <tr><td colSpan="4" style={{textAlign:'center',color:'#a07850',padding:'32px'}}>No reservations yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ================================================================
   GALLERY MANAGER
================================================================ */
function GalleryManager({ token, addToast }) {
  const [photos, setPhotos]   = useState([]);
  const [albums, setAlbums]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [tab, setTab]         = useState('url');   // 'url' | 'upload' | 'album'
  const [saving, setSaving]   = useState(false);
  const [preview, setPreview] = useState('');
  const cfg = authApi(token);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        api.get('/api/gallery', cfg),
        api.get('/api/gallery/albums', cfg),
      ]);
      setPhotos(p.data);
      setAlbums(a.data);
    } catch {}
    setLoading(false);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  function openModal() { setModal(true); setTab('url'); setPreview(''); }
  function closeModal() { setModal(false); setPreview(''); }

  async function handleAdd(e) {
    e.preventDefault(); setSaving(true);
    const form = e.target;

    try {
      if (tab === 'album') {
        const name = form.album_name.value.trim();
        const url  = form.album_url.value.trim();
        if (!name || !url) { addToast('Name and URL are required.', 'error'); setSaving(false); return; }
        await api.post('/api/gallery/albums', { name, url }, cfg);
        addToast('Album added.', 'success');
      } else if (tab === 'upload') {
        const file = form.photo.files[0];
        if (!file) { addToast('Select a file.', 'error'); setSaving(false); return; }
        const fd = new FormData();
        fd.append('photo', file);
        fd.append('caption', form.caption.value);
        fd.append('sort_order', form.sort_order?.value || 0);
        await api.post('/api/gallery', fd, { headers: { Authorization: `Bearer ${token}` } });
        addToast('Photo added.', 'success');
      } else {
        const url = form.url.value.trim();
        if (!url) { addToast('Enter a URL.', 'error'); setSaving(false); return; }
        await api.post('/api/gallery', { url, caption: form.caption.value, sort_order: form.sort_order?.value || 0 }, cfg);
        addToast('Photo added.', 'success');
      }
      closeModal(); load();
    } catch (err) { addToast(err.response?.data?.error || 'Failed.', 'error'); }
    setSaving(false);
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this photo?')) return;
    try { await api.delete(`/api/gallery/${id}`, cfg); addToast('Deleted.', 'success'); load(); }
    catch { addToast('Failed.', 'error'); }
  }

  async function handleDeleteAlbum(id) {
    if (!window.confirm('Delete this album?')) return;
    try { await api.delete(`/api/gallery/albums/${id}`, cfg); addToast('Album deleted.', 'success'); load(); }
    catch { addToast('Failed.', 'error'); }
  }

  function imgSrc(url) {
    return url.startsWith('http') ? url : `https://muki684.com${url}`;
  }

  return (
    <>
      <div className="page-actions">
        <button className="admin-btn admin-btn-primary" onClick={openModal}>
          <i className="fa-solid fa-plus" /> Add Photo / Album
        </button>
      </div>

      {loading
        ? <div className="admin-loading"><i className="fa-solid fa-circle-notch fa-spin" /></div>
        : (photos.length === 0 && albums.length === 0)
          ? <div className="admin-card"><div style={{padding:'40px',textAlign:'center',color:'var(--a-text-dim)'}}>No photos or albums yet.</div></div>
          : (
            <>
              {albums.length > 0 && (
                <div className="admin-card" style={{marginBottom:'20px'}}>
                  <div className="admin-card-header"><h3>Photo Albums</h3></div>
                  <table className="admin-table">
                    <thead><tr><th>Album Name</th><th>URL</th><th>Added</th><th>Actions</th></tr></thead>
                    <tbody>
                      {albums.map(a => (
                        <tr key={a.id}>
                          <td>
                            <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                              <i className="fa-solid fa-images" style={{color:'var(--a-accent)'}} />
                              <strong>{a.name}</strong>
                            </div>
                          </td>
                          <td>
                            <a href={a.url} target="_blank" rel="noreferrer"
                              style={{fontSize:'0.78rem',color:'var(--a-accent)',wordBreak:'break-all'}}>
                              {a.url.length > 60 ? a.url.slice(0,60) + '…' : a.url}
                            </a>
                          </td>
                          <td>{fmtDate(a.created_at)}</td>
                          <td className="action-cell">
                            <button className="btn-icon btn-delete" onClick={() => handleDeleteAlbum(a.id)}>
                              <i className="fa-solid fa-trash" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {photos.length > 0 && (
                <div className="gallery-admin-grid">
                  {photos.map(p => (
                    <div key={p.id} className="gallery-admin-item">
                      <img src={imgSrc(p.url)} alt={p.caption || ''} />
                      <div className="gallery-admin-overlay">
                        {p.caption && <span>{p.caption}</span>}
                        <button className="btn-icon btn-delete" onClick={() => handleDelete(p.id)}>
                          <i className="fa-solid fa-trash" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )
      }

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal-box">
            <div className="modal-header">
              <h3>Add to Gallery</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <div className="login-tabs" style={{marginBottom:'20px'}}>
              <button className={`login-tab${tab==='url'    ? ' active':''}`} onClick={() => { setTab('url');    setPreview(''); }}>Link / URL</button>
              <button className={`login-tab${tab==='upload' ? ' active':''}`} onClick={() => { setTab('upload'); setPreview(''); }}>Upload File</button>
              <button className={`login-tab${tab==='album'  ? ' active':''}`} onClick={() => { setTab('album');  setPreview(''); }}>Photo Album</button>
            </div>

            <form onSubmit={handleAdd}>
              {tab === 'album' ? (
                <>
                  <div className="admin-form-group">
                    <label>Album Name *</label>
                    <input name="album_name" required placeholder="e.g. Our Coffee Bar" />
                  </div>
                  <div className="admin-form-group">
                    <label>Album URL *</label>
                    <input name="album_url" required
                      placeholder="https://photos.google.com/share/… or any album link" />
                  </div>
                  <p style={{fontSize:'0.78rem',color:'var(--a-text-dim)',margin:'-8px 0 16px'}}>
                    Paste a shared album link (Google Photos, Flickr, etc.). Visitors can open it in a frame or directly.
                  </p>
                </>
              ) : tab === 'url' ? (
                <div className="admin-form-group">
                  <label>Image URL *</label>
                  <input name="url" placeholder="https://example.com/photo.jpg"
                    onChange={e => setPreview(e.target.value)} />
                </div>
              ) : (
                <div className="admin-form-group">
                  <label>Photo File *</label>
                  <input name="photo" type="file" accept="image/*"
                    onChange={e => {
                      const f = e.target.files[0];
                      if (f) setPreview(URL.createObjectURL(f));
                    }} />
                </div>
              )}

              {tab !== 'album' && (
                <>
                  {preview && (
                    <div style={{marginBottom:'16px',borderRadius:'6px',overflow:'hidden',maxHeight:'200px'}}>
                      <img src={preview} alt="preview"
                        style={{width:'100%',height:'200px',objectFit:'cover',display:'block'}} />
                    </div>
                  )}
                  <div className="admin-form-row">
                    <div className="admin-form-group">
                      <label>Caption</label>
                      <input name="caption" placeholder="Optional description" />
                    </div>
                    <div className="admin-form-group">
                      <label>Sort Order</label>
                      <input name="sort_order" type="number" defaultValue="0" min="0" />
                    </div>
                  </div>
                </>
              )}

              <div className="modal-actions">
                <button type="button" className="admin-btn admin-btn-outline" onClick={closeModal}>Cancel</button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : tab === 'album' ? 'Add Album' : 'Add Photo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

/* ================================================================
   REVIEWS MANAGER
================================================================ */
function ReviewsManager({ token, addToast }) {
  const [list, setList]       = useState([]);
  const [loading, setLoading] = useState(true);
  const cfg = authApi(token);

  const { search, onSearch, page, setPage, paged, filtered, totalPages } = usePaginatedSearch(
    list,
    (r, q) => r.customer_name?.toLowerCase().includes(q) || r.customer_email?.toLowerCase().includes(q) || r.message?.toLowerCase().includes(q)
  );

  const load = useCallback(async () => {
    setLoading(true);
    try { setList((await api.get('/api/reviews/all', cfg)).data); }
    catch {}
    setLoading(false);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function toggleApprove(id, current) {
    try {
      await api.put(`/api/reviews/${id}/approve`, {}, cfg);
      setList(p => p.map(r => r.id === id ? { ...r, approved: current ? 0 : 1 } : r));
      addToast(current ? 'Review hidden.' : 'Review approved.', 'success');
    } catch { addToast('Update failed.', 'error'); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this review?')) return;
    try { await api.delete(`/api/reviews/${id}`, cfg); addToast('Deleted.', 'success'); load(); }
    catch { addToast('Failed.', 'error'); }
  }

  function Stars({ rating }) {
    return (
      <span style={{color:'#c4522a',fontSize:'0.78rem',letterSpacing:'1px'}}>
        {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
      </span>
    );
  }

  return (
    <div className="admin-card">
      <div className="table-toolbar">
        <TableSearch value={search} onChange={onSearch} placeholder="Search by name, email or review…" />
      </div>
      {loading
        ? <div className="admin-loading"><i className="fa-solid fa-circle-notch fa-spin" /></div>
        : <>
            <table className="admin-table">
              <thead>
                <tr><th>Name</th><th>Rating</th><th>Review</th><th>Date</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {paged.map(r => (
                  <tr key={r.id}>
                    <td><strong>{r.customer_name}</strong>{r.customer_email && <><br /><span style={{fontSize:'0.76rem',color:'var(--a-text-dim)'}}>{r.customer_email}</span></>}</td>
                    <td><Stars rating={r.rating} /></td>
                    <td className="msg-cell">{r.message}</td>
                    <td>{fmtDate(r.created_at)}</td>
                    <td>
                      <span className={`badge ${r.approved ? 'badge-confirmed' : 'badge-pending'}`}>
                        {r.approved ? 'Published' : 'Pending'}
                      </span>
                    </td>
                    <td className="action-cell">
                      <button className="btn-icon btn-edit" title={r.approved ? 'Hide' : 'Approve'}
                        onClick={() => toggleApprove(r.id, r.approved)}>
                        <i className={`fa-solid ${r.approved ? 'fa-eye-slash' : 'fa-check'}`} />
                      </button>
                      <button className="btn-icon btn-delete" onClick={() => handleDelete(r.id)}>
                        <i className="fa-solid fa-trash" />
                      </button>
                    </td>
                  </tr>
                ))}
                {!paged.length && <tr><td colSpan="6" style={{textAlign:'center',padding:'32px',color:'var(--a-text-dim)'}}>{search ? 'No matching reviews.' : 'No reviews yet.'}</td></tr>}
              </tbody>
            </table>
            <Pagination page={page} totalPages={totalPages} setPage={setPage} total={list.length} filtered={filtered.length} />
          </>
      }
    </div>
  );
}

/* ================================================================
   ORDERS MANAGER
================================================================ */
function OrdersManager({ token, addToast }) {
  const [list, setList]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail]   = useState(null);
  const cfg = authApi(token);

  const { search, onSearch, page, setPage, paged, filtered, totalPages } = usePaginatedSearch(
    list,
    (o, q) => String(o.id).includes(q) || o.customer_name?.toLowerCase().includes(q) || o.customer_email?.toLowerCase().includes(q)
  );

  const load = useCallback(async () => {
    setLoading(true);
    try { setList((await api.get('/api/orders', cfg)).data); }
    catch {}
    setLoading(false);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function openDetail(id) {
    try {
      const { data } = await api.get(`/api/orders/${id}`, cfg);
      setDetail(data);
    } catch { addToast('Failed to load order.', 'error'); }
  }

  async function updateStatus(id, status) {
    try {
      await api.put(`/api/orders/${id}/status`, { status }, cfg);
      setList(p => p.map(o => o.id === id ? { ...o, status } : o));
      if (detail?.id === id) setDetail(d => ({ ...d, status }));
      addToast('Status updated.', 'success');
    } catch { addToast('Update failed.', 'error'); }
  }

  const statusOpts = ['pending','preparing','ready','completed','cancelled'];

  return (
    <>
      <div className="admin-card">
        <div className="table-toolbar">
          <TableSearch value={search} onChange={onSearch} placeholder="Search by ID, name or email…" />
        </div>
        {loading
          ? <div className="admin-loading"><i className="fa-solid fa-circle-notch fa-spin" /></div>
          : <>
              <table className="admin-table">
                <thead>
                  <tr><th>ID</th><th>Customer</th><th>Items</th><th>Total</th><th>Status</th><th>Date</th><th></th></tr>
                </thead>
                <tbody>
                  {paged.map(o => (
                    <tr key={o.id}>
                      <td><strong>#{o.id}</strong></td>
                      <td><strong>{o.customer_name}</strong>{o.customer_email && <><br /><span style={{fontSize:'0.78rem',color:'var(--a-text-dim)'}}>{o.customer_email}</span></>}</td>
                      <td>{o.item_count}</td>
                      <td>${parseFloat(o.total).toFixed(2)}</td>
                      <td>
                        <select className={`status-select status-order-${o.status}`} value={o.status}
                          onChange={e => updateStatus(o.id, e.target.value)}>
                          {statusOpts.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                        </select>
                      </td>
                      <td>{fmtDate(o.created_at)}</td>
                      <td className="action-cell">
                        <button className="btn-icon btn-edit" onClick={() => openDetail(o.id)} title="View items">
                          <i className="fa-solid fa-eye" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!paged.length && <tr><td colSpan="7" style={{textAlign:'center',padding:'32px',color:'var(--a-text-dim)'}}>{search ? 'No matching orders.' : 'No orders yet.'}</td></tr>}
                </tbody>
              </table>
              <Pagination page={page} totalPages={totalPages} setPage={setPage} total={list.length} filtered={filtered.length} />
            </>
        }
      </div>

      {detail && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDetail(null)}>
          <div className="modal-box">
            <div className="modal-header">
              <h3>Order #{detail.id} — {detail.customer_name}</h3>
              <button className="modal-close" onClick={() => setDetail(null)}>×</button>
            </div>
            <table className="admin-table" style={{marginBottom:'16px'}}>
              <thead><tr><th>Item</th><th>Qty</th><th>Price</th><th>Subtotal</th></tr></thead>
              <tbody>
                {detail.items?.map(i => (
                  <tr key={i.id}>
                    <td><strong>{i.item_name}</strong></td>
                    <td>{i.quantity}</td>
                    <td>${parseFloat(i.item_price).toFixed(2)}</td>
                    <td>${(i.item_price * i.quantity).toFixed(2)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan="3" style={{textAlign:'right',fontWeight:'500'}}>Total</td>
                  <td><strong>${parseFloat(detail.total).toFixed(2)}</strong></td>
                </tr>
              </tbody>
            </table>
            {detail.notes && <p style={{fontSize:'0.84rem',color:'var(--a-text-mid)',marginBottom:'16px'}}><strong>Notes:</strong> {detail.notes}</p>}
            <div className="modal-actions">
              <select className="status-select" value={detail.status}
                onChange={e => updateStatus(detail.id, e.target.value)}
                style={{borderRadius:'6px',padding:'8px 28px 8px 10px',border:'1px solid var(--a-border)'}}>
                {statusOpts.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
              <button className="admin-btn admin-btn-outline" onClick={() => setDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ================================================================
   MENU MANAGER
================================================================ */
function MenuManager({ token, addToast }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const cfg = authApi(token);

  const { search, onSearch, page, setPage, paged, filtered, totalPages } = usePaginatedSearch(
    items,
    (item, q) => item.name?.toLowerCase().includes(q) || item.category?.toLowerCase().includes(q) || item.description?.toLowerCase().includes(q)
  );

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get('/api/menu/all', cfg)).data); }
    catch {}
    setLoading(false);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function handleSave(e) {
    e.preventDefault(); setSaving(true);
    const d = Object.fromEntries(new FormData(e.target));
    const payload = { name: d.name, description: d.description, price: parseFloat(d.price), category: d.category, available: d.available === '1' };
    try {
      if (modal.item) await api.put(`/api/menu/${modal.item.id}`, payload, cfg);
      else            await api.post('/api/menu', payload, cfg);
      addToast(modal.item ? 'Item updated.' : 'Item added.', 'success');
      setModal(null); load();
    } catch (err) { addToast(err.response?.data?.error || 'Failed.', 'error'); }
    setSaving(false);
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this menu item?')) return;
    try { await api.delete(`/api/menu/${id}`, cfg); addToast('Item deleted.', 'success'); load(); }
    catch { addToast('Failed to delete.', 'error'); }
  }

  return (
    <>
      <div className="page-actions">
        <button className="admin-btn admin-btn-primary" onClick={() => setModal({ item: null })}>
          <i className="fa-solid fa-plus" /> Add Item
        </button>
      </div>
      <div className="admin-card">
        <div className="table-toolbar">
          <TableSearch value={search} onChange={onSearch} placeholder="Search by name, category or description…" />
        </div>
        {loading
          ? <div className="admin-loading"><i className="fa-solid fa-circle-notch fa-spin" /></div>
          : <>
              <table className="admin-table">
                <thead><tr><th>Name</th><th>Category</th><th>Price</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {paged.map(item => (
                    <tr key={item.id}>
                      <td><strong>{item.name}</strong></td>
                      <td><span className={`badge cat-badge cat-${item.category}`}>{catLabel(item.category)}</span></td>
                      <td>${parseFloat(item.price).toFixed(2)}</td>
                      <td><span className={`badge ${item.available ? 'badge-confirmed' : 'badge-hidden'}`}>{item.available ? 'Active' : 'Hidden'}</span></td>
                      <td className="action-cell">
                        <button className="btn-icon btn-edit" onClick={() => setModal({ item })}>
                          <i className="fa-solid fa-pen" />
                        </button>
                        <button className="btn-icon btn-delete" onClick={() => handleDelete(item.id)}>
                          <i className="fa-solid fa-trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!paged.length && <tr><td colSpan="5" style={{textAlign:'center',color:'#a07850',padding:'32px'}}>{search ? 'No matching items.' : 'No menu items yet.'}</td></tr>}
                </tbody>
              </table>
              <Pagination page={page} totalPages={totalPages} setPage={setPage} total={items.length} filtered={filtered.length} />
            </>
        }
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal-box">
            <div className="modal-header">
              <h3>{modal.item ? 'Edit Menu Item' : 'Add Menu Item'}</h3>
              <button className="modal-close" onClick={() => setModal(null)}>×</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="admin-form-group">
                <label>Name *</label>
                <input name="name" required defaultValue={modal.item?.name} />
              </div>
              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label>Price ($) *</label>
                  <input name="price" type="number" step="0.01" min="0" required defaultValue={modal.item?.price} />
                </div>
                <div className="admin-form-group">
                  <label>Category *</label>
                  <select name="category" required defaultValue={modal.item?.category || MENU_CATS[0]}>
                    {MENU_CATS.map(c => (
                      <option key={c} value={c}>{catLabel(c)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="admin-form-group">
                <label>Description</label>
                <textarea name="description" rows={3} defaultValue={modal.item?.description} />
              </div>
              <div className="admin-form-group">
                <label>Visibility</label>
                <select name="available" defaultValue={modal.item?.available !== false ? '1' : '0'}>
                  <option value="1">Active — visible on menu</option>
                  <option value="0">Hidden</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="admin-btn admin-btn-outline" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

/* ================================================================
   RESERVATIONS MANAGER
================================================================ */
function ReservationsManager({ token, addToast }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const cfg = authApi(token);

  const { search, onSearch, page, setPage, paged, filtered, totalPages } = usePaginatedSearch(
    list,
    (r, q) => r.name?.toLowerCase().includes(q) || r.email?.toLowerCase().includes(q) || r.date?.includes(q) || r.phone?.includes(q)
  );

  const load = useCallback(async () => {
    setLoading(true);
    try { setList((await api.get('/api/reservations', cfg)).data); }
    catch {}
    setLoading(false);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function updateStatus(id, status) {
    try {
      await api.put(`/api/reservations/${id}/status`, { status }, cfg);
      setList(p => p.map(r => r.id === id ? { ...r, status } : r));
      addToast('Status updated.', 'success');
    } catch { addToast('Update failed.', 'error'); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this reservation?')) return;
    try { await api.delete(`/api/reservations/${id}`, cfg); addToast('Deleted.', 'success'); load(); }
    catch { addToast('Failed.', 'error'); }
  }

  return (
    <div className="admin-card">
      <div className="table-toolbar">
        <TableSearch value={search} onChange={onSearch} placeholder="Search by name, email, date or phone…" />
      </div>
      {loading
        ? <div className="admin-loading"><i className="fa-solid fa-circle-notch fa-spin" /></div>
        : <>
            <table className="admin-table">
              <thead><tr><th>Guest</th><th>Email</th><th>Date & Time</th><th>Guests</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {paged.map(r => (
                  <tr key={r.id}>
                    <td><strong>{r.name}</strong></td>
                    <td>{r.email}</td>
                    <td>{fmtDate(r.date)} {r.time?.slice(0,5)}</td>
                    <td>{r.guests}</td>
                    <td>
                      <select className={`status-select status-${r.status}`} value={r.status}
                        onChange={e => updateStatus(r.id, e.target.value)}>
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td className="action-cell">
                      <button className="btn-icon btn-delete" onClick={() => handleDelete(r.id)}>
                        <i className="fa-solid fa-trash" />
                      </button>
                    </td>
                  </tr>
                ))}
                {!paged.length && <tr><td colSpan="6" style={{textAlign:'center',color:'#a07850',padding:'32px'}}>{search ? 'No matching reservations.' : 'No reservations yet.'}</td></tr>}
              </tbody>
            </table>
            <Pagination page={page} totalPages={totalPages} setPage={setPage} total={list.length} filtered={filtered.length} />
          </>
      }
    </div>
  );
}

/* ================================================================
   CONTACTS MANAGER
================================================================ */
function ContactsManager({ token, addToast }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const cfg = authApi(token);

  const { search, onSearch, page, setPage, paged, filtered, totalPages } = usePaginatedSearch(
    list,
    (c, q) => c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.message?.toLowerCase().includes(q)
  );

  const load = useCallback(async () => {
    setLoading(true);
    try { setList((await api.get('/api/contact', cfg)).data); }
    catch {}
    setLoading(false);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function handleDelete(id) {
    if (!window.confirm('Delete this message?')) return;
    try { await api.delete(`/api/contact/${id}`, cfg); addToast('Deleted.', 'success'); load(); }
    catch { addToast('Failed.', 'error'); }
  }

  return (
    <div className="admin-card">
      <div className="table-toolbar">
        <TableSearch value={search} onChange={onSearch} placeholder="Search by name, email or message…" />
      </div>
      {loading
        ? <div className="admin-loading"><i className="fa-solid fa-circle-notch fa-spin" /></div>
        : <>
            <table className="admin-table">
              <thead><tr><th>Name</th><th>Email</th><th>Message</th><th>Date</th><th>Actions</th></tr></thead>
              <tbody>
                {paged.map(c => (
                  <tr key={c.id}>
                    <td><strong>{c.name}</strong></td>
                    <td>{c.email}</td>
                    <td className="msg-cell">{c.message}</td>
                    <td>{fmtDate(c.created_at)}</td>
                    <td className="action-cell">
                      <button className="btn-icon btn-delete" onClick={() => handleDelete(c.id)}>
                        <i className="fa-solid fa-trash" />
                      </button>
                    </td>
                  </tr>
                ))}
                {!paged.length && <tr><td colSpan="5" style={{textAlign:'center',color:'#a07850',padding:'32px'}}>{search ? 'No matching messages.' : 'No messages yet.'}</td></tr>}
              </tbody>
            </table>
            <Pagination page={page} totalPages={totalPages} setPage={setPage} total={list.length} filtered={filtered.length} />
          </>
      }
    </div>
  );
}

/* ================================================================
   MENU PDF MANAGER
================================================================ */
function MenuPdfManager({ token, addToast }) {
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const cfg = authApi(token);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/menu-pdf', cfg);
      setCurrent(data || null);
    } catch { setCurrent(null); }
    setLoading(false);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function upload(file) {
    if (!file || file.type !== 'application/pdf') {
      addToast('Please select a valid PDF file.', 'error'); return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('pdf', file);
    try {
      await api.post('/api/menu-pdf', fd, { headers: { Authorization: `Bearer ${token}` } });
      addToast('Menu PDF uploaded successfully.', 'success');
      load();
    } catch (err) { addToast(err.response?.data?.error || 'Upload failed.', 'error'); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function handleDelete() {
    if (!window.confirm('Remove the current menu PDF?')) return;
    try {
      await api.delete('/api/menu-pdf', cfg);
      addToast('PDF removed.', 'success');
      setCurrent(null);
    } catch { addToast('Failed.', 'error'); }
  }

  return (
    <>
      <div className="admin-card" style={{ marginBottom: '20px' }}>
        <div className="admin-card-header">
          <h3>Upload Menu PDF</h3>
        </div>
        <p style={{ fontSize: '0.82rem', color: 'var(--a-text-dim)', marginBottom: '16px' }}>
          Customers will see a "Download Menu" button on the website linking to this file.
        </p>

        <div
          className={`pdf-drop-zone${dragOver ? ' drag-over' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files[0]); }}
          onClick={() => !uploading && fileRef.current?.click()}
        >
          <input ref={fileRef} type="file" accept=".pdf,application/pdf"
            style={{ display: 'none' }} onChange={e => upload(e.target.files[0])} />
          {uploading ? (
            <>
              <i className="fa-solid fa-circle-notch fa-spin pdf-drop-icon" />
              <p>Uploading…</p>
            </>
          ) : (
            <>
              <i className="fa-solid fa-file-pdf pdf-drop-icon" />
              <p><strong>Click to browse</strong> or drag & drop a PDF here</p>
              <span className="pdf-drop-hint">PDF files only · Max 20 MB</span>
            </>
          )}
        </div>
      </div>

      {loading
        ? <div className="admin-loading"><i className="fa-solid fa-circle-notch fa-spin" /></div>
        : current && (
          <div className="admin-card">
            <div className="admin-card-header"><h3>Current Menu PDF</h3></div>
            <div className="pdf-current-row">
              <div className="pdf-current-info">
                <i className="fa-solid fa-file-pdf" />
                <div>
                  <strong>{current.filename || 'menu.pdf'}</strong>
                  {current.size && <span>{(current.size / 1024).toFixed(0)} KB</span>}
                  {current.uploaded_at && <span>Uploaded {fmtDate(current.uploaded_at)}</span>}
                </div>
              </div>
              <div className="pdf-current-actions">
                <a href="https://muki684.com/api/menu-pdf/view" target="_blank" rel="noreferrer"
                  className="admin-btn admin-btn-outline">
                  <i className="fa-solid fa-eye" /> Preview
                </a>
                <button className="admin-btn admin-btn-danger" onClick={handleDelete}>
                  <i className="fa-solid fa-trash" /> Remove
                </button>
              </div>
            </div>

            <div className="pdf-link-box">
              <p className="pdf-link-label">
                <i className="fa-solid fa-qrcode" /> Direct link — use this URL to generate a QR code
              </p>
              <div className="pdf-link-row">
                <input
                  readOnly
                  value="https://muki684.com/api/menu-pdf/view"
                  className="pdf-link-input"
                  onFocus={e => e.target.select()}
                />
                <button
                  className="admin-btn admin-btn-outline"
                  onClick={() => {
                    navigator.clipboard.writeText('https://muki684.com/api/menu-pdf/view');
                  }}
                  title="Copy link">
                  <i className="fa-solid fa-copy" /> Copy
                </button>
              </div>
            </div>
          </div>
        )
      }
    </>
  );
}

/* ================================================================
   QR CODE GENERATOR
================================================================ */
const QR_PRESETS = [
  { label: 'Menu PDF',       url: 'https://muki684.com/api/menu-pdf/view' },
  { label: 'Website',        url: 'https://muki684.com' },
  { label: 'Instagram',      url: 'https://www.instagram.com/muki684cafe' },
  { label: 'Reservations',   url: 'https://muki684.com/#reservations' },
  { label: 'Google Maps',    url: 'https://maps.google.com/?q=Muki+684+Cafe+Kacyiru+Kigali' },
  { label: 'Google Review', url: 'https://www.google.com/maps/search/Muki+684+Cafe+Kacyiru+Kigali+Rwanda' },
];

function QrGenerator() {
  const [items, setItems] = useState(QR_PRESETS.map(p => ({ ...p, canvas: null })));
  const [custom, setCustom] = useState({ label: '', url: '' });
  const canvasRefs = useRef([]);

  useEffect(() => {
    items.forEach((item, i) => {
      if (!item.url || !canvasRefs.current[i]) return;
      QRCode.toCanvas(canvasRefs.current[i], item.url, {
        width: 200,
        margin: 2,
        color: { dark: '#3F2305', light: '#ffffff' },
      }).catch(() => {});
    });
  }, [items]);

  function addCustom() {
    if (!custom.label.trim() || !custom.url.trim()) return;
    setItems(prev => [...prev, { label: custom.label.trim(), url: custom.url.trim(), canvas: null }]);
    setCustom({ label: '', url: '' });
  }

  function removeItem(idx) {
    setItems(prev => prev.filter((_, i) => i !== idx));
    canvasRefs.current.splice(idx, 1);
  }

  function downloadQr(idx, label) {
    const canvas = canvasRefs.current[idx];
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `qr-${label.toLowerCase().replace(/\s+/g, '-')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }

  return (
    <div className="qr-page">
      <div className="qr-grid">
        {items.map((item, i) => (
          <div key={i} className="qr-card">
            <div className="qr-card-label">{item.label}</div>
            <canvas ref={el => { canvasRefs.current[i] = el; }} className="qr-canvas" />
            <div className="qr-card-url" title={item.url}>{item.url}</div>
            <div className="qr-card-actions">
              <button className="admin-btn" onClick={() => downloadQr(i, item.label)}>
                <i className="fa-solid fa-download" /> Download PNG
              </button>
              <button className="admin-btn-danger" onClick={() => removeItem(i)}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="qr-custom-box">
        <h3 className="qr-custom-title"><i className="fa-solid fa-plus" /> Add custom QR</h3>
        <div className="qr-custom-row">
          <input
            className="admin-input"
            placeholder="Label (e.g. WhatsApp)"
            value={custom.label}
            onChange={e => setCustom(p => ({ ...p, label: e.target.value }))}
          />
          <input
            className="admin-input"
            placeholder="URL or text"
            value={custom.url}
            onChange={e => setCustom(p => ({ ...p, url: e.target.value }))}
          />
          <button className="admin-btn" onClick={addCustom}>Generate</button>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
   ADMIN PAGE
================================================================ */
export default function Admin() {
  const [token, setToken]       = useState(() => localStorage.getItem('muki_token'));
  const [page, setPage]         = useState('overview');
  const [sidebarOpen, setOpen]  = useState(false);
  const [toasts, addToast]      = useToast();

  const titles = { overview:'Overview', orders:'Orders', gallery:'Gallery', reviews:'Reviews', menu:'Menu Management', reservations:'Reservations', contacts:'Contact Messages', 'menu-pdf':'Menu PDF', 'qr-codes':'QR Codes' };
  const handleLogin  = t => { localStorage.setItem('muki_token', t); setToken(t); };
  const handleLogout = () => { localStorage.removeItem('muki_token'); setToken(null); };

  if (!token) return <LoginScreen onLogin={handleLogin} />;

  return (
    <div className="admin-layout">
      <Sidebar page={page} setPage={setPage} onLogout={handleLogout} open={sidebarOpen} setOpen={setOpen} />
      <div className="admin-main">
        <header className="admin-header">
          <button className="hamburger" onClick={() => setOpen(true)}>
            <i className="fa-solid fa-bars" />
          </button>
          <h1 className="admin-title">{titles[page]}</h1>
        </header>
        <div className="admin-content">
          {page === 'overview'     && <Overview token={token} />}
          {page === 'orders'       && <OrdersManager token={token} addToast={addToast} />}
          {page === 'gallery'      && <GalleryManager token={token} addToast={addToast} />}
          {page === 'reviews'      && <ReviewsManager token={token} addToast={addToast} />}
          {page === 'menu'         && <MenuManager token={token} addToast={addToast} />}
          {page === 'reservations' && <ReservationsManager token={token} addToast={addToast} />}
          {page === 'contacts'     && <ContactsManager token={token} addToast={addToast} />}
          {page === 'menu-pdf'     && <MenuPdfManager token={token} addToast={addToast} />}
          {page === 'qr-codes'     && <QrGenerator />}
        </div>
      </div>
      <Toasts list={toasts} />
    </div>
  );
}
